import { useEffect, useState, useMemo, Fragment } from "react";
import api from "../lib/api";
import { formatEUR, formatEURDecimal, SPANISH_MONTHS_SHORT } from "../lib/format";
import { Plus, Trash2, ZoomIn, ZoomOut, Download, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const QUINCENAS = [];
for (let m = 1; m <= 12; m++) {
  for (let q = 1; q <= 2; q++) QUINCENAS.push({ key: `${m}-${q}`, month: m, quincena: q });
}

function NewEntryDialog({ open, onClose, year, onCreated }) {
  const [form, setForm] = useState({
    project_code: "",
    project_name: "",
    type: "Gasto",
    amount: "",
    month: 1,
    quincena: 1,
    concepto: "",
    proveedor: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_code.trim()) return toast.error("Indica el código de proyecto");
    if (!form.amount || isNaN(Number(form.amount))) return toast.error("Importe inválido");
    try {
      await api.post("/cashflow/entries", {
        ...form,
        amount: Math.abs(Number(form.amount)),
        year,
        month: Number(form.month),
        quincena: Number(form.quincena),
      });
      toast.success("Entrada creada");
      onCreated();
      onClose();
    } catch (e) {
      toast.error("Error al crear entrada");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="new-entry-overlay">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white border border-[var(--border)] w-full max-w-lg p-6"
        data-testid="new-entry-dialog"
      >
        <h3 className="font-display font-black text-xl mb-4">Nueva entrada manual</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num">Código proyecto</span>
            <input data-testid="entry-project-code" className="border border-[var(--border)] px-3 py-2 font-mono-num focus:outline-none focus:border-black" value={form.project_code} onChange={set("project_code")} />
          </label>
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num">Nombre proyecto</span>
            <input data-testid="entry-project-name" className="border border-[var(--border)] px-3 py-2 focus:outline-none focus:border-black" value={form.project_name} onChange={set("project_name")} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num">Tipo</span>
            <select data-testid="entry-type" className="border border-[var(--border)] px-3 py-2 focus:outline-none focus:border-black" value={form.type} onChange={set("type")}>
              <option value="Ingreso">Ingreso</option>
              <option value="Gasto">Gasto</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num">Importe (€)</span>
            <input data-testid="entry-amount" type="number" step="0.01" className="border border-[var(--border)] px-3 py-2 font-mono-num focus:outline-none focus:border-black" value={form.amount} onChange={set("amount")} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num">Mes</span>
            <select data-testid="entry-month" className="border border-[var(--border)] px-3 py-2 focus:outline-none focus:border-black" value={form.month} onChange={set("month")}>
              {SPANISH_MONTHS_SHORT.map((m, i) => <option key={i} value={i+1}>{i+1}. {m}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num">Quincena</span>
            <select data-testid="entry-quincena" className="border border-[var(--border)] px-3 py-2 focus:outline-none focus:border-black" value={form.quincena} onChange={set("quincena")}>
              <option value={1}>1Q (día 15)</option>
              <option value={2}>2Q (día 30)</option>
            </select>
          </label>
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num">Concepto</span>
            <input data-testid="entry-concept" className="border border-[var(--border)] px-3 py-2 focus:outline-none focus:border-black" value={form.concepto} onChange={set("concepto")} />
          </label>
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num">Proveedor / Cliente</span>
            <input data-testid="entry-vendor" className="border border-[var(--border)] px-3 py-2 focus:outline-none focus:border-black" value={form.proveedor} onChange={set("proveedor")} />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="border border-[var(--border)] px-4 py-2 text-sm hover:bg-[var(--hover)]" data-testid="entry-cancel">Cancelar</button>
          <button type="submit" className="bg-black text-white px-4 py-2 text-sm hover:bg-gray-800" data-testid="entry-save">Guardar</button>
        </div>
      </form>
    </div>
  );
}

function ProjectDetailDialog({ open, onClose, project, year, onChange }) {
  const entries = useMemo(() => {
    if (!project) return [];
    const all = [];
    Object.entries(project.cells).forEach(([k, v]) => {
      (v.entries || []).forEach(e => all.push(e));
    });
    return all.sort((a, b) => a.month - b.month || a.quincena - b.quincena);
  }, [project]);
  if (!open || !project) return null;

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta entrada?")) return;
    try {
      await api.delete(`/cashflow/entries/${id}`);
      toast.success("Entrada eliminada");
      onChange();
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="project-detail-overlay">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[var(--border)] w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">{project.project_code}</div>
            <h3 className="font-display font-black text-xl">{project.project_name || project.project_code}</h3>
          </div>
          <button onClick={onClose} className="text-sm border border-[var(--border)] px-3 py-1.5 hover:bg-[var(--hover)]" data-testid="project-close">Cerrar</button>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-[var(--hover)] text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num">
              <tr>
                <th className="text-left px-3 py-2">Quincena</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-left px-3 py-2">Concepto</th>
                <th className="text-left px-3 py-2">Proveedor</th>
                <th className="text-right px-3 py-2">Importe</th>
                <th className="text-right px-3 py-2">Origen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className="border-t border-[var(--border)] hover:bg-[var(--hover)]" data-testid={`entry-row-${e.id}`}>
                  <td className="px-3 py-2 font-mono-num">{SPANISH_MONTHS_SHORT[e.month-1]} · Q{e.quincena}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase font-mono-num px-1.5 py-0.5 ${e.type === "Ingreso" ? "bg-emerald-700 text-white" : "bg-red-700 text-white"}`}>{e.type}</span>
                  </td>
                  <td className="px-3 py-2">{e.concepto || "—"}</td>
                  <td className="px-3 py-2 text-[var(--ink-muted)]">{e.proveedor || "—"}</td>
                  <td className={`px-3 py-2 text-right font-mono-num ${e.type === "Ingreso" ? "text-emerald-700" : "text-red-700"}`}>
                    {e.type === "Ingreso" ? "+" : "−"}{formatEURDecimal(e.amount)}
                  </td>
                  <td className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-mono-num text-[var(--ink-muted)]">{e.source}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => handleDelete(e.id)} className="text-[var(--ink-muted)] hover:text-red-700" data-testid={`delete-entry-${e.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Cashflow() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailProject, setDetailProject] = useState(null);
  const [includePrevisto, setIncludePrevisto] = useState(true);
  const [includeHistorical, setIncludeHistorical] = useState(() => localStorage.getItem("cashflow_include_historical") === "true");
  const [syncOpen, setSyncOpen] = useState(false);
  const [zoom, setZoom] = useState(() => {
    const v = parseFloat(localStorage.getItem("cashflow_zoom") || "0.85");
    return isNaN(v) ? 0.85 : v;
  });
  const [showPastMonths, setShowPastMonths] = useState(() => {
    return localStorage.getItem("cashflow_show_past") === "true";
  });

  const setZoomSafe = (next) => {
    const clamped = Math.max(0.55, Math.min(1.4, next));
    setZoom(clamped);
    localStorage.setItem("cashflow_zoom", String(clamped));
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/cashflow?year=${year}&include_previsto=${includePrevisto}&include_historical=${includeHistorical}`),
      api.get("/cashflow/years"),
    ]).then(([cf, yr]) => {
      setData(cf.data);
      const list = yr.data.years || [];
      if (!list.includes(year)) list.push(year);
      list.sort((a, b) => a - b);
      setYears(list);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, includePrevisto, includeHistorical]);

  // Recargar cuando el auto-sync de Layout termine
  useEffect(() => {
    const h = () => load();
    window.addEventListener("proyectos:synced", h);
    return () => window.removeEventListener("proyectos:synced", h);
    // eslint-disable-next-line
  }, [year, includePrevisto, includeHistorical]);

  const projects = data?.projects || [];

  // Filter visible quincenas: hide quincenas strictly older than the current real-world quincena
  // unless user enables showPastMonths.
  const visibleQuincenas = useMemo(() => {
    if (showPastMonths || !data?.current_quincena || year !== data.current_quincena.year) {
      return QUINCENAS;
    }
    const cur = data.current_quincena;
    return QUINCENAS.filter(q => q.month > cur.month || (q.month === cur.month && q.quincena >= cur.quincena));
  }, [showPastMonths, data, year]);

  const togglePast = (v) => {
    setShowPastMonths(v);
    localStorage.setItem("cashflow_show_past", String(v));
  };

  return (
    <div className="p-8" data-testid="cashflow-page">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">Cashflow</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Proyectos × Quincenas.</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center border border-[var(--border)] bg-white" data-testid="zoom-controls">
            <button
              type="button"
              onClick={() => setZoomSafe(zoom - 0.1)}
              className="px-2 py-2 hover:bg-[var(--hover)]"
              title="Reducir"
              data-testid="zoom-out"
            >
              <ZoomOut size={14} />
            </button>
            <span className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)] px-2 min-w-[42px] text-center" data-testid="zoom-value">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setZoomSafe(zoom + 0.1)}
              className="px-2 py-2 hover:bg-[var(--hover)]"
              title="Ampliar"
              data-testid="zoom-in"
            >
              <ZoomIn size={14} />
            </button>
          </div>
          <label className="flex items-center gap-2 text-xs" data-testid="toggle-past-wrapper">
            <input
              type="checkbox"
              checked={showPastMonths}
              onChange={(e) => togglePast(e.target.checked)}
              data-testid="toggle-past"
            />
            <span>Mostrar meses anteriores</span>
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={includePrevisto}
              onChange={(e) => setIncludePrevisto(e.target.checked)}
              data-testid="toggle-previsto"
            />
            <span>Incluir previsiones</span>
          </label>
          <label className="flex items-center gap-2 text-xs" title="Muestra las facturas ya cobradas y gastos ya pagados en su quincena real. Por defecto se ocultan para no ensuciar la previsión.">
            <input
              type="checkbox"
              checked={includeHistorical}
              onChange={(e) => { const v = e.target.checked; setIncludeHistorical(v); localStorage.setItem("cashflow_include_historical", String(v)); }}
              data-testid="toggle-historical"
            />
            <span>Mostrar cobros/pagos realizados</span>
          </label>
          <select
            data-testid="cashflow-year"
            className="border border-[var(--border)] bg-white px-3 py-2 text-sm font-mono-num focus:outline-none focus:border-black"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            data-testid="sync-proyectos-button"
            onClick={() => setSyncOpen(true)}
            className="border border-[var(--border)] bg-white text-sm px-4 py-2 inline-flex items-center gap-2 hover:bg-[var(--hover)]"
            title="Sincronizar movimientos desde la API de Proyectos (Teamleader → Proyectos → Cashflow)"
          >
            <RefreshCw size={14}/> Sync Proyectos
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-sm text-[var(--ink-muted)] font-mono-num" data-testid="cashflow-loading">Cargando…</div>
      ) : projects.length === 0 && (!data.fixed_expenses || data.fixed_expenses.every(g => g.total === 0)) ? (
        <div className="border border-dashed border-[var(--border)] bg-white p-16 text-center" data-testid="cashflow-empty">
          <h3 className="font-display text-xl font-bold mb-2">Aún no hay movimientos en {year}</h3>
          <p className="text-sm text-[var(--ink-muted)] mb-6">Pulsa <b>Sync Proyectos</b> para traer los ingresos y gastos desde la app de Proyectos.</p>
          <div className="flex justify-center gap-2">
            <button onClick={() => setSyncOpen(true)} className="bg-black text-white px-4 py-2 text-sm inline-flex items-center gap-2"><RefreshCw size={14}/> Sync Proyectos</button>
          </div>
        </div>
      ) : (
        <div className="border border-[var(--border)] bg-white overflow-auto max-h-[78vh]">
          <table
            className="cashflow-grid text-xs w-max min-w-full"
            style={{ zoom }}
            data-testid="cashflow-table"
          >
            <thead>
              <tr>
                <th className="sticky-col text-left px-3 py-3 font-mono-num text-[10px] uppercase tracking-widest min-w-[280px]">Proyecto</th>
                {visibleQuincenas.map(q => (
                  <th key={q.key} className="px-2 py-3 font-mono-num text-[10px] uppercase tracking-widest text-right min-w-[88px]">
                    <div>{SPANISH_MONTHS_SHORT[q.month-1]}</div>
                    <div className="text-[var(--ink-muted)]">Q{q.quincena}</div>
                  </th>
                ))}
                <th className="px-3 py-3 font-mono-num text-[10px] uppercase tracking-widest text-right min-w-[110px]">Total</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(p => {
                const totalIngreso = p.total_ingreso + (p.total_ingreso_previsto || 0);
                const totalGasto = p.total_gasto + (p.total_gasto_previsto || 0);
                const projectNet = totalIngreso - totalGasto;
                return (
                  <Fragment key={p.project_code}>
                    {/* Project header row (clickable to open detail) */}
                    <tr
                      className="bg-[var(--hover)] hover:bg-zinc-200/70 cursor-pointer border-t-2 border-[var(--ink)]"
                      onClick={() => setDetailProject(p)}
                      data-testid={`project-row-${p.project_code}`}
                    >
                      <td className="sticky-col bg-[var(--hover)] px-3 py-2" colSpan={1}>
                        <div className="min-w-0">
                          <div className="font-mono-num text-[10px] uppercase tracking-wider text-[var(--ink-muted)]">{p.project_code}</div>
                          <div className="text-xs font-bold truncate max-w-[260px]" title={p.project_name}>{p.project_name || "—"}</div>
                        </div>
                      </td>
                      {visibleQuincenas.map(q => <td key={q.key} className="px-2 py-2"></td>)}
                      <td className={`px-3 py-2 text-right font-mono-num font-bold ${projectNet >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                        {projectNet >= 0 ? "+" : "−"}{formatEUR(Math.abs(projectNet))}
                      </td>
                    </tr>
                    {/* Ingreso row */}
                    <tr className="bg-white">
                      <td className="sticky-col bg-white px-3 py-1 pl-6 text-[10px] uppercase tracking-widest font-mono-num text-emerald-800" data-testid={`ingreso-row-${p.project_code}`}>Ingreso</td>
                      {visibleQuincenas.map(q => {
                        const cell = p.cells[q.key];
                        const real = cell?.Ingreso || 0;
                        const prev = cell?.Ingreso_previsto || 0;
                        return (
                          <td key={q.key} className="px-2 py-1 text-right font-mono-num">
                            {real ? <div className="text-emerald-700">{formatEUR(real)}</div> : null}
                            {prev ? <div className="text-emerald-700/60 italic text-[10px]" title="Previsto">~{formatEUR(prev)}</div> : null}
                          </td>
                        );
                      })}
                      <td className="px-3 py-1 text-right font-mono-num font-bold text-emerald-700">
                        {p.total_ingreso ? formatEUR(p.total_ingreso) : ""}
                        {p.total_ingreso_previsto ? <div className="text-emerald-700/60 italic text-[10px] font-normal">~{formatEUR(p.total_ingreso_previsto)}</div> : null}
                      </td>
                    </tr>
                    {/* Gasto row */}
                    <tr className="bg-white">
                      <td className="sticky-col bg-white px-3 py-1 pl-6 text-[10px] uppercase tracking-widest font-mono-num text-red-800" data-testid={`gasto-row-${p.project_code}`}>Gasto</td>
                      {visibleQuincenas.map(q => {
                        const cell = p.cells[q.key];
                        const real = cell?.Gasto || 0;
                        const prev = cell?.Gasto_previsto || 0;
                        return (
                          <td key={q.key} className="px-2 py-1 text-right font-mono-num">
                            {real ? <div className="text-red-700">{formatEUR(real)}</div> : null}
                            {prev ? <div className="text-red-700/60 italic text-[10px]" title="Previsto">~{formatEUR(prev)}</div> : null}
                          </td>
                        );
                      })}
                      <td className="px-3 py-1 text-right font-mono-num font-bold text-red-700">
                        {p.total_gasto ? formatEUR(p.total_gasto) : ""}
                        {p.total_gasto_previsto ? <div className="text-red-700/60 italic text-[10px] font-normal">~{formatEUR(p.total_gasto_previsto)}</div> : null}
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
              {/* Gastos Fijos block - collapsed by section */}
              {data.fixed_expenses && data.fixed_expenses.length > 0 && (
                <Fragment>
                  <tr className="bg-zinc-800 text-white">
                    <td className="sticky-col bg-zinc-800 text-white px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-bold" colSpan={visibleQuincenas.length + 2}>
                      Estructura / Impuestos
                    </td>
                  </tr>
                  {data.fixed_expenses.map((fe, i) => (
                    <tr key={i} className="bg-white" data-testid={`fixed-group-${i}`}>
                      <td className="sticky-col bg-white px-3 py-2">
                        <div className="text-xs font-bold tracking-wide">{fe.name}</div>
                        {fe.by_category && fe.by_category.length > 0 && (
                          <div className="text-[10px] text-[var(--ink-muted)] font-mono-num truncate">
                            {fe.by_category.map(c => c.category.split("/")[0]).join(" · ")}
                          </div>
                        )}
                      </td>
                      {visibleQuincenas.map(q => {
                        const v = fe.cells[q.key] || 0;
                        return <td key={q.key} className="px-2 py-2 text-right font-mono-num text-red-700">{v ? `−${formatEUR(v)}` : ""}</td>;
                      })}
                      <td className="px-3 py-2 text-right font-mono-num font-bold text-red-700">
                        {fe.total ? `−${formatEUR(fe.total)}` : ""}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              )}
              {/* Total Ingresos row */}
              <tr className="bg-emerald-50 border-t-2 border-[var(--ink)]" data-testid="row-total-ingresos">
                <td className="sticky-col bg-emerald-50 px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-bold text-emerald-800">Total Ingresos</td>
                {visibleQuincenas.map(q => {
                  const t = data.column_totals[q.key];
                  const v = (t?.Ingreso || 0) + (t?.Ingreso_previsto || 0);
                  return (
                    <td key={q.key} className="px-2 py-2 text-right font-mono-num font-bold text-emerald-700">
                      {v === 0 ? "" : "+" + formatEUR(v)}
                    </td>
                  );
                })}
                <td className="px-3 py-2"></td>
              </tr>
              {/* Total Gastos row */}
              <tr className="bg-red-50" data-testid="row-total-gastos">
                <td className="sticky-col bg-red-50 px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-bold text-red-800">Total Gastos</td>
                {visibleQuincenas.map(q => {
                  const t = data.column_totals[q.key];
                  const v = (t?.Gasto || 0) + (t?.Gasto_previsto || 0) + (t?.GastoFijo || 0);
                  return (
                    <td key={q.key} className="px-2 py-2 text-right font-mono-num font-bold text-red-700">
                      {v === 0 ? "" : "−" + formatEUR(v)}
                    </td>
                  );
                })}
                <td className="px-3 py-2"></td>
              </tr>
              {/* Balance row (Ingresos - Gastos) */}
              <tr className="bg-zinc-100 border-t border-[var(--ink)]" data-testid="row-balance">
                <td className="sticky-col bg-zinc-100 px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-bold">Balance</td>
                {visibleQuincenas.map(q => {
                  const v = data.column_totals[q.key]?.Neto || 0;
                  return (
                    <td key={q.key} className={`px-2 py-2 text-right font-mono-num font-bold ${v >= 0 ? "text-emerald-700" : v < 0 ? "text-red-700" : ""}`}>
                      {v === 0 ? "" : (v >= 0 ? "+" : "−") + formatEUR(Math.abs(v))}
                    </td>
                  );
                })}
                <td className="px-3 py-2"></td>
              </tr>
              {/* Cumulative row */}
              <tr className="bg-black text-white sticky bottom-0">
                <td className="sticky-col bg-black text-white px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-black">Cash flow</td>
                {data.cumulative.filter(c => visibleQuincenas.some(q => q.key === c.key)).map(c => (
                  <td key={c.key} className={`px-2 py-2 text-right font-mono-num font-bold ${c.value >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                    {formatEUR(c.value)}
                  </td>
                ))}
                <td className="px-3 py-2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <ProjectDetailDialog open={!!detailProject} onClose={() => setDetailProject(null)} project={detailProject} year={year} onChange={() => { setDetailProject(null); load(); }} />
      <SyncProyectosDialog open={syncOpen} onClose={() => setSyncOpen(false)} year={year} onSynced={load} />
    </div>
  );
}


function SyncProyectosDialog({ open, onClose, year, onSynced }) {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) { setStatus(null); setResult(null); return; }
    setLoadingStatus(true);
    api.get("/integration/proyectos/status")
      .then(r => setStatus(r.data))
      .catch(e => setStatus({ configured: false, detail: e?.response?.data?.detail || e.message }))
      .finally(() => setLoadingStatus(false));
  }, [open]);

  const doSync = async () => {
    setSyncing(true);
    setResult(null);
    try {
      const r = await api.post(`/integration/proyectos/sync?year=${year}`);
      setResult({ ok: true, ...r.data });
      toast.success(`${r.data.inserted} movimientos sincronizados`);
      onSynced?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Error";
      setResult({ ok: false, detail });
      toast.error(detail);
    } finally { setSyncing(false); }
  };

  if (!open) return null;
  const notConfigured = status && status.configured === false;
  const httpOk = status?.http_status === 200;
  const totales = status?.totales;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="sync-proyectos-overlay">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[var(--border)] w-full max-w-lg" data-testid="sync-proyectos-dialog">
        <div className="p-5 border-b border-[var(--border)]">
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Integración</div>
          <h3 className="font-display font-black text-xl">Sincronizar con la API de Proyectos</h3>
          <p className="text-xs text-[var(--ink-muted)] mt-1">Trae los movimientos de facturas y gastos del año {year} desde la app de Proyectos y sustituye lo actual con <span className="font-mono-num">source=proyectos</span>. Los movimientos manuales y los gastos fijos NO se tocan.</p>
        </div>
        <div className="p-5 space-y-4">
          {loadingStatus && <div className="text-xs text-[var(--ink-muted)]"><Loader2 className="inline animate-spin mr-2" size={12}/>Comprobando conexión…</div>}
          {notConfigured && (
            <div className="border border-rose-300 bg-rose-50 text-rose-800 p-3 text-xs rounded" data-testid="sync-not-configured">
              <div className="font-semibold mb-1">Integración no configurada</div>
              <div>{status.detail}</div>
              <div className="mt-2 text-[10px] font-mono-num">Faltan <code>PROY_BACKEND_URL</code> y/o <code>INTEGRATION_API_KEY</code> en las variables de entorno del backend.</div>
            </div>
          )}
          {status && status.configured && (
            <div className={`border p-3 text-xs rounded ${httpOk ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="sync-status">
              <div className="font-semibold mb-1">
                {httpOk ? "Conexión OK" : `Problema con la API (${status.http_status || "?"})`}
              </div>
              <div className="text-[10px] font-mono-num text-[var(--ink-muted)] break-all">{status.proy_backend_url}</div>
              {httpOk && totales && (
                <div className="mt-2 grid grid-cols-3 gap-2 font-mono-num">
                  <div><div className="text-[10px] uppercase opacity-60">Ingresos</div><div className="font-bold">{formatEURDecimal(totales.ingresos)}</div></div>
                  <div><div className="text-[10px] uppercase opacity-60">Gastos</div><div className="font-bold">{formatEURDecimal(totales.gastos)}</div></div>
                  <div><div className="text-[10px] uppercase opacity-60">Movimientos</div><div className="font-bold">{totales.movimientos}</div></div>
                </div>
              )}
              {!httpOk && status.detail && <div className="mt-2 font-mono-num text-[10px]">{status.detail}</div>}
            </div>
          )}
          {result && (
            <div className={`border p-3 text-xs rounded ${result.ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-rose-300 bg-rose-50 text-rose-800"}`} data-testid="sync-result">
              {result.ok ? (
                <>
                  <div className="font-semibold">Sincronización completada ✓</div>
                  <div className="mt-1 font-mono-num text-[11px]">
                    Insertadas: <b>{result.inserted}</b> · Reemplazadas: <b>{result.deleted}</b> · Ignoradas: <b>{result.skipped}</b>
                    <br/>Remotos recibidos: {result.remote_count}
                    {result.excel_dup_deleted > 0 && (
                      <><br/>Duplicados Excel eliminados: <b data-testid="excel-dup-deleted">{result.excel_dup_deleted}</b></>
                    )}
                  </div>
                </>
              ) : (
                <><div className="font-semibold mb-1">No se pudo sincronizar</div><div>{result.detail}</div></>
              )}
            </div>
          )}
        </div>
        <div className="p-5 border-t border-[var(--border)] flex justify-end gap-2">
          <button onClick={onClose} className="border border-[var(--border)] bg-white text-sm px-4 py-2 hover:bg-[var(--hover)]" data-testid="sync-cancel">Cerrar</button>
          <button
            data-testid="sync-execute"
            onClick={doSync}
            disabled={syncing || notConfigured || !httpOk}
            className="bg-black text-white text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-40"
          >
            {syncing ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
            {syncing ? "Sincronizando…" : `Sincronizar ${year}`}
          </button>
        </div>
      </div>
    </div>
  );
}
